"use client";
import React from "react";
import { cellRadiusAt } from "../../lib/brand/cell";

// ─────────────────────────────────────────────────────────────────────────────
// CellIcons — security glyphs that live INSIDE the organism's "cell" shape.
//
// The cell is the BRAND cell — the canonical silhouette from lib/brand/cell.ts,
// the same curve the avatar's clip-path and netti's own body wear (law_cell).
// Each security glyph is clipped to the cell interior so it reads as "subtracted
// into" the cell. Plus a padlock built from the same cell shape as its body.
//
// All strokes are currentColor so the landing can colour them. Stance keys match
// PrivIcons / STANCES in LandingSections.tsx exactly: rls · hash · oauth · lock ·
// gdpr · circle. Self-contained — no globals.css needed.
// ─────────────────────────────────────────────────────────────────────────────

/** The 6 stance keys, aligned 1:1 with STANCES in LandingSections.tsx. */
export const CELL_STANCE_KEYS = ["rls", "hash", "oauth", "lock", "gdpr", "circle"] as const;
export type CellSymbol = (typeof CELL_STANCE_KEYS)[number];

/** the cell's own height/width ratio, read straight off the brand curve (≈0.775). */
const Y_ASPECT = cellRadiusAt(Math.PI / 2);

/**
 * Build the BRAND cell outline as an SVG path string — sampled off the shared
 * polar law (lib/brand/cell.ts) so this glyph set can never drift from the
 * avatar or from netti's own body. 48 sample points, Catmull-Rom smoothed to a
 * closed cubic-Bézier path.
 *
 * @param cx      centre x
 * @param cy      centre y
 * @param r       radius (half-width); the cell's own ~0.776 aspect sets the height
 * @param n       sample count (default 48)
 * @param squash  extra vertical squash on top of the brand aspect (default 1)
 */
export function cellPath(
  cx: number,
  cy: number,
  r: number,
  n = 48,
  squash = 1,
): string {
  const pts: Array<[number, number]> = [];
  for (let i = 0; i < n; i++) {
    const t = (i / n) * Math.PI * 2;
    const rad = cellRadiusAt(t) * r;
    pts.push([cx + Math.cos(t) * rad, cy + Math.sin(t) * rad * squash]);
  }
  return catmullRomClosed(pts);
}

/** Closed Catmull-Rom → cubic Bézier path (no wobble, just a smooth outline). */
function catmullRomClosed(pts: Array<[number, number]>): string {
  const n = pts.length;
  if (n < 2) return "";
  const f = (v: number) => Math.round(v * 1000) / 1000;
  let d = `M${f(pts[0][0])},${f(pts[0][1])}`;
  for (let i = 0; i < n; i++) {
    const p0 = pts[(i - 1 + n) % n];
    const p1 = pts[i];
    const p2 = pts[(i + 1) % n];
    const p3 = pts[(i + 2) % n];
    const c1x = p1[0] + (p2[0] - p0[0]) / 6;
    const c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6;
    const c2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += `C${f(c1x)},${f(c1y)} ${f(c2x)},${f(c2y)} ${f(p2[0])},${f(p2[1])}`;
  }
  return d + "Z";
}

// Standard 24×24 cell geometry used by the icons.
const VB = 24;
const CX = 12;
const CY = 12;
const CELL_R = 9; // half-width; vertical extent ≈ 9 * 0.74 ≈ 6.66
const CELL_D = cellPath(CX, CY, CELL_R);

const STROKE = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.5,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

// The inner glyph for each stance — kept simple, stroke-based, legible small.
// Rendered clipped to the cell interior so it reads as subtracted into the cell.
const GLYPHS: Record<CellSymbol, React.ReactNode> = {
  // row-level security — table lines with a lock notion (rows answering to you)
  rls: (
    <>
      <path d="M6 8.4h12M6 12h12M6 15.6h12" />
      <path d="M12 6.5v11" />
    </>
  ),
  // tokens, hashed — hash marks
  hash: (
    <path d="M10.3 6.5 9 17.5M15 6.5l-1.3 11M6.8 9.7h10.4M6.2 14.3h10.4" />
  ),
  // oauth 2.1 — shield-check
  oauth: (
    <>
      <path d="M12 5.6l5 2.1v2.5c0 3.1-2.1 5-5 6-2.9-1-5-2.9-5-6V7.7l5-2.1Z" />
      <path d="M9.7 11.7l1.6 1.6 3-3.2" />
    </>
  ),
  // encrypted at rest — key / lock dots
  lock: (
    <>
      <rect x="8.5" y="11" width="7" height="5.4" rx="1.4" />
      <path d="M9.9 11V9.6a2.1 2.1 0 0 1 4.2 0V11" />
      <circle cx="12" cy="13.5" r="0.5" fill="currentColor" stroke="none" />
    </>
  ),
  // gdpr, honoured — shield
  gdpr: (
    <path d="M12 5.4v13.2M7.4 8.2 12 5.9l4.6 2.3M7.4 8.2v5.4L12 16.5l4.6-2.9V8.2" />
  ),
  // disclosure classes — concentric rings (circles of trust)
  circle: (
    <>
      <ellipse cx="12" cy="12" rx="6.4" ry="4.7" />
      <ellipse cx="12" cy="12" rx="3" ry="2.2" />
    </>
  ),
};

export interface CellIconProps {
  /** Which stance glyph to render inside the cell. */
  symbol: CellSymbol;
  /** Pixel size (width & height). Defaults to 24. */
  size?: number;
  className?: string;
}

// React.useId is stable across SSR + client (a module counter diverged between
// the two render passes and threw a hydration mismatch). Strip colons so the id
// is a safe SVG url(#…) fragment.
function useClipId(prefix: string): string {
  return `${prefix}-${React.useId().replace(/:/g, "")}`;
}

/**
 * A security glyph sitting inside the organism cell squircle. The cell outline
 * is stroked; the inner glyph is clipped to the cell interior so it reads as
 * subtracted into the cell.
 */
export function CellIcon({ symbol, size = 24, className }: CellIconProps) {
  const clipId = useClipId("cellclip");
  return (
    <svg
      viewBox={`0 0 ${VB} ${VB}`}
      width={size}
      height={size}
      className={className}
      aria-hidden
    >
      <defs>
        <clipPath id={clipId}>
          <path d={CELL_D} />
        </clipPath>
      </defs>
      {/* the cell body outline */}
      <path d={CELL_D} {...STROKE} />
      {/* the glyph, contained within the cell */}
      <g {...STROKE} clipPath={`url(#${clipId})`}>
        {GLYPHS[symbol]}
      </g>
    </svg>
  );
}

export interface PadlockCellProps {
  /** Pixel size (width & height). Defaults to 24. */
  size?: number;
  className?: string;
  /** Draw a second concentric shackle arc for weight. Default true. */
  doubleShackle?: boolean;
}

/**
 * The hero mark for the security section header. The brand cell is the lock
 * BODY; the shackle is a semicircular arc matching the cell's own aspect so body
 * and shackle read as one organic form.
 */
export function PadlockCell({ size = 24, className, doubleShackle = true }: PadlockCellProps) {
  // Body: a cell sitting in the lower portion of the viewBox.
  const bodyCy = 15;
  const bodyR = 8;
  const bodyD = cellPath(CX, bodyCy, bodyR);
  const bodyTop = bodyCy - bodyR * Y_ASPECT; // ≈ 15 - 5.92 = 9.08

  // Shackle: a squashed semicircular arc (aspect-matched) rising from the body.
  // Outer arc.
  const outerRx = 4.6;
  const outerRy = 4.6 * Y_ASPECT + 1.6; // taller than a pure squash so it clears the body
  const arcTop = bodyTop - 0.4;
  const outerLeft = CX - outerRx;
  const outerRight = CX + outerRx;
  const outerArc = `M${outerLeft},${arcTop} A${outerRx},${outerRy} 0 0 1 ${outerRight},${arcTop}`;

  // Inner arc (the hollow of the shackle) — a thinner, lower parallel arc.
  const innerRx = 2.7;
  const innerRy = 2.7 * Y_ASPECT + 1.2;
  const innerLeft = CX - innerRx;
  const innerRight = CX + innerRx;
  const innerArc = `M${innerLeft},${arcTop} A${innerRx},${innerRy} 0 0 1 ${innerRight},${arcTop}`;

  return (
    <svg
      viewBox={`0 0 ${VB} ${VB}`}
      width={size}
      height={size}
      className={className}
      aria-hidden
    >
      {/* shackle first so the body sits over its feet */}
      <path d={outerArc} {...STROKE} />
      {doubleShackle && <path d={innerArc} {...STROKE} />}
      {/* the cell as the lock body */}
      <path d={bodyD} {...STROKE} />
      {/* keyhole */}
      <g {...STROKE}>
        <circle cx={CX} cy={bodyCy - 0.4} r={1} />
        <path d={`M${CX} ${bodyCy + 0.4} v2`} />
      </g>
    </svg>
  );
}

export default CellIcon;
